import Pica from "pica"
import { prepareImageUrlRequest } from "./request"
import axios from "axios"
import type { HttpHeaders } from "../../../api/types"
import { CropCoords, CroppedVariant, CropType, ImageOptions, ImageUploadData } from "./types"
import { CropVariantResult } from "./components/types"
import { Maybe } from "@figurepos/fgr-lib-backend/lib/types"
import * as uuid from "uuid"
import { loadImageAsync } from "../../../utils/loadImage"

export const pica = Pica()

const CANVAS_OUTPUT_TYPES = ["image/jpeg", "image/png", "image/webp"]

const FILE_EXTENSION_BY_TYPE: Record<string, string> = {
    "image/jpeg": ".jpg",
    "image/png": ".png",
    "image/webp": ".webp",
}

export const resolveOutputType = (sourceType: string, forceJpeg?: boolean): string => {
    if (forceJpeg) {
        return "image/jpeg"
    }
    return CANVAS_OUTPUT_TYPES.includes(sourceType) ? sourceType : "image/png"
}

export const getOutputFileName = (fileName: string, sourceType: string, outputType: string): string =>
    sourceType === outputType ? fileName : changeFileNameExtension(fileName, FILE_EXTENSION_BY_TYPE[outputType])

export const validateAndProcessImage = async (
    file: File,
    imageOptions: ImageOptions | null,
    maxFileSize: number,
    onSizeInvalid: () => void
) => {
    let fileRes = file

    const sizeMB = fileRes.size / (1024 * 1024)

    // Validate image min size/dimensions
    const { minWidth, minHeight } = imageOptions ?? {}
    const isSizeValid = minWidth || minHeight ? await validateImgSize(file, minWidth ?? 0, minHeight ?? 0) : true
    if (!isSizeValid) {
        onSizeInvalid()
    }

    // If image is .heic iOS format, then convert to .jpg
    if (fileRes.type === "image/heic") {
        const heic2any = (await import("heic2any")).default
        const jpgBlob = (await heic2any({
            blob: file,
            quality: 0.9,
            toType: "image/jpeg",
        })) as Blob

        const fileName = changeFileNameExtension(file.name, ".jpg")
        fileRes = new File([jpgBlob], fileName, { type: "image/jpeg" })
    }
    // If image exceeds 'maxFileSize' (eg. photos saved as .png) or 'forceJpeg' is set, then convert to .jpg
    const fileType = resolveOutputType(fileRes.type, imageOptions?.forceJpeg || sizeMB > maxFileSize)
    const fileName = getOutputFileName(fileRes.name, fileRes.type, fileType)
    const { maxWidth = 3000, maxHeight = 2000 } = imageOptions ?? {}

    // Process image: convert, downscale
    return processImage(fileRes, maxWidth, maxHeight, fileType, fileName)
}

const processImage = (
    file: File,
    maxWidth: number,
    maxHeight: number,
    fileType: string,
    fileName: string
): Promise<File> => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas")
        const img = new Image()

        img.onload = async e => {
            const image = e.target as HTMLImageElement

            // If image dimensions are exceeding, calculate new proportional size
            const dimensions = downscaleSizeToCover(image.width, image.height, maxWidth, maxHeight)

            // Prepare target canvas with target dimensions for 'pica' lib
            canvas.width = dimensions.width
            canvas.height = dimensions.height

            try {
                // Resize, change type, and apply compression with 'pica' lib
                const resized = await pica.resize(img, canvas)
                if (fileType === "image/jpeg") {
                    fillTransparentBackground(resized)
                }
                const blob = await pica.toBlob(resized, fileType, 0.75)
                const newFile = new File([blob], fileName, { type: fileType })

                // Delete img from memory
                URL.revokeObjectURL(image.src)

                resolve(newFile)
            } catch (e) {
                reject(e)
            }
        }
        img.onerror = reject
        img.src = URL.createObjectURL(file)
    })
}

// JPEG has no alpha channel - transparent pixels would turn black, so paint white behind them
export const fillTransparentBackground = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        return
    }
    ctx.globalCompositeOperation = "destination-over"
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
}

export const validateImgSize = (file: File, minWidth: number, minHeight: number): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        const img = new Image()

        img.onload = e => {
            const { naturalWidth: imgWidth, naturalHeight: imgHeight } = e.target as HTMLImageElement
            resolve(imgWidth >= minWidth && imgHeight >= minHeight)
        }
        img.onerror = reject
        img.src = URL.createObjectURL(file)
    })
}

export const downscaleSizeToCover = (
    srcWidth: number,
    srcHeight: number,
    targetWidth: number,
    targetHeight: number
): { width: number; height: number } => {
    const ratio =
        srcWidth > targetWidth && srcHeight > targetHeight
            ? Math.max(targetWidth / srcWidth, targetHeight / srcHeight)
            : 1
    return { width: srcWidth * ratio, height: srcHeight * ratio }
}

export const changeFileNameExtension = (fileName: string, newExtension: string) => {
    const dotIdx = fileName.lastIndexOf(".")
    return dotIdx > -1 ? fileName.slice(0, fileName.lastIndexOf(".")) + newExtension : fileName + newExtension
}

export const UNSUPPORTED_BROWSER_ERROR_CODE = "ERR_GET_IMAGE_DATA"

export const requestFileUpload = async (
    file: File,
    headers: HttpHeaders,
    setProgress: (progress: number) => void
): Promise<string> => {
    const res = await prepareImageUrlRequest(headers, {
        filename: file.name,
        fileType: file.type,
    })
    const fileUrl = res.url
    const uploadUrl = res.signedRequest

    await axios.put(uploadUrl, file, {
        headers: {
            "Content-Type": file.type,
        },
        onUploadProgress: evt => setProgress(Math.round((evt.loaded / (evt.total || 1)) * 100)),
    })

    return fileUrl
}

export const convertUploadedImageToFile = async (imgUrl: string) => {
    const res = await fetch(imgUrl)
    const blob = await res.blob()
    const fileName = imgUrl.substring(imgUrl.lastIndexOf("/") + 1)
    const fileType = blob.type || "image/jpeg" // default to jpeg if can't determine

    return new File([blob], fileName, { type: fileType })
}

const isSameCropCoords = (a: CropCoords, b: CropCoords) =>
    a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height

const getUrlFileExtension = (url: string) => {
    const path = url.split(/[?#]/)[0]
    return path.match(/\.[^./]+$/)?.[0]?.toLowerCase() ?? null
}

const isJpegExtension = (ext: string | null) => ext === ".jpg" || ext === ".jpeg"

export const isCroppedVariantReusable = async (
    prevVariant: CroppedVariant,
    newCropData: CropVariantResult
): Promise<boolean> => {
    if (
        !prevVariant.url ||
        !prevVariant.cropCoords ||
        !isSameCropCoords(prevVariant.cropCoords, newCropData.cropCoords)
    ) {
        return false
    }

    const prevExtension = getUrlFileExtension(prevVariant.url)
    const newExtension = getUrlFileExtension(newCropData.file.name)
    const isSameFormat =
        prevExtension === newExtension || (isJpegExtension(prevExtension) && isJpegExtension(newExtension))
    if (!isSameFormat) {
        return false
    }

    try {
        const prevImage = await loadImageAsync(prevVariant.url)
        return prevImage.naturalWidth === newCropData.width && prevImage.naturalHeight === newCropData.height
    } catch {
        // Can't verify previous image - re-upload to be safe
        return false
    }
}

export const getCropVariantByType = (image: ImageUploadData, cropType: string): CroppedVariant | null => {
    const variant = image.croppedVariants?.find(v => v.cropType === cropType)
    if (variant) {
        return variant
    }
    return null
}

export const getAspectRatioFromCropType = (cropType: CropType): number | null => {
    switch (cropType) {
        case CropType.Ratio_2_1:
            return 2 / 1
        case CropType.Ratio_1_1:
            return 1 / 1
        case CropType.Ratio_4_3:
            return 4 / 3
        case CropType.Ratio_1300_590:
            return 1300 / 590
        case CropType.Free:
        default:
            return null
    }
}

export const getFirstCroppedVariantUrl = (data: Maybe<ImageUploadData[]>): string | null =>
    data?.[0]?.croppedVariants?.[0]?.url ?? null

export const renameFilesToUUID = (files: File[]): File[] =>
    files.map(file => {
        const ext = file.name.match(/\.[^.]+$/)?.[0] ?? ""
        return new File([file], `${uuid.v7()}${ext}`, {
            type: file.type,
            lastModified: file.lastModified,
        })
    })

export const sanitizeFileNames = (files: File[]): File[] =>
    files.map(file => new File([file], file.name.replace(/ /g, "_"), { type: file.type }))
